/**
 * One extracted entry, and what the human decided about it.
 *
 * THIS is where "correcting must be as easy as confirming" is actually decided,
 * so the reasoning belongs here rather than in a design document:
 *
 *  · There is no edit mode. The three dispositions are one radio group —
 *    «stimmt» · «korrigieren» · «nicht lesbar» — rendered at the same size, in
 *    the same weight, in the same place, for every entry. Confirming is not a
 *    prominent button with a small "edit" link beside it, because that layout
 *    IS the rubber stamp.
 *  · Nothing is preselected. There is no default a fast reader can accept by
 *    pressing one key, and the row cannot be committed until entries have been
 *    decided one by one.
 *  · The correction field is always there and always live. Typing in it selects
 *    «korrigieren» by itself, so correcting an entry costs exactly one action —
 *    typing the right number — while confirming costs one click. Correction is
 *    if anything the shorter path for someone who is reading the source.
 *  · «nicht lesbar» is a peer of the other two, not a footnote. An unread value
 *    is recorded as unread; it never becomes a zero and never becomes the
 *    extraction's guess by default (BRIEF: null over guess).
 *  · An entry without a checkable citation gets NO controls at all — just the
 *    sentence saying why. That is why "confirm without a citation" is not a
 *    state this UI can reach.
 *  · For a session that may not verify (`readOnly` — every non-founder, Founder
 *    decision 2026-09-16) the entry shows the read value and its page and
 *    nothing to press. The sentence explaining who verifies stands once per
 *    row and once per screen, not under every cell.
 */
import { useI18n } from '@/i18n'
import type { Disposition } from '@/api/types'

export interface Decision {
  disposition: Disposition
  /** What the reader typed. Only meaningful for `corrected`. */
  raw: string
}

const ORDER: Disposition[] = ['confirmed', 'corrected', 'unreadable']

const LABEL: Record<Disposition, 'verify.confirm' | 'verify.correct' | 'verify.unreadable'> = {
  confirmed: 'verify.confirm',
  corrected: 'verify.correct',
  unreadable: 'verify.unreadable',
}

export function DispositionField({
  name,
  label,
  hint,
  note,
  extracted,
  unit,
  hasCitation,
  page,
  decision,
  onChange,
  readOnly = false,
}: {
  /** Unique per entry — the radio group's name and the input's id stem. */
  name: string
  label: string
  hint?: string
  /**
   * A second line about the READING of this value rather than about the entry:
   * the notation a Ziffer is printed in and what it normalises to, or the term
   * the document itself uses. It sits next to the number, because that is where
   * a person checking the cell against the page is looking.
   */
  note?: string | null
  /** The value as it was read, already formatted (or the "not read" marker). */
  extracted: string
  unit?: string | null
  hasCitation: boolean
  page?: number | null
  decision: Decision | undefined
  onChange: (next: Decision | undefined) => void
  /** The session may look but not decide: no radios, no field, no cancel. */
  readOnly?: boolean
}) {
  const { t } = useI18n()
  const inputId = `${name}-value`

  return (
    <div
      className="flex flex-col gap-2 border-b border-border py-3 last:border-b-0"
      data-testid="disposition-field"
      data-entry={name}
      data-disposition={decision?.disposition ?? 'undecided'}
      data-has-citation={hasCitation}
      data-readonly={readOnly}
    >
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="text-[13px] font-medium text-ink-2">{label}</span>
        <span className="num text-[13px] text-ink" data-testid="extracted-value">
          {extracted}
          {unit ? <span className="text-muted-2"> {unit}</span> : null}
        </span>
        {typeof page === 'number' ? (
          <span className="num text-[12px] text-muted-2">{t('verify.page', { page })}</span>
        ) : null}
      </div>
      {hint ? <p className="text-[12px] text-muted">{hint}</p> : null}
      {note ? (
        <p className="text-[12px] text-muted" data-testid="entry-note">
          {note}
        </p>
      ) : null}

      {!hasCitation ? (
        <p className="text-[12px] text-muted" data-testid="no-citation">
          {t('verify.noCitation')}
        </p>
      ) : readOnly ? null : (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <fieldset className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <legend className="sr-only">
              {t('verify.disposition')} — {label}
            </legend>
            {ORDER.map((disposition) => (
              <label
                key={disposition}
                className="flex items-center gap-1.5 text-[13px] text-ink"
              >
                <input
                  type="radio"
                  name={name}
                  value={disposition}
                  checked={decision?.disposition === disposition}
                  onChange={() =>
                    onChange({
                      disposition,
                      // Switching away from a correction drops the typed value:
                      // a stale number must not travel with a "matches".
                      raw: disposition === 'corrected' ? (decision?.raw ?? '') : '',
                    })
                  }
                  className="accent-accent"
                />
                {t(LABEL[disposition])}
              </label>
            ))}
          </fieldset>

          {/* Always live. Typing here IS choosing «korrigieren» — one action. */}
          <label htmlFor={inputId} className="sr-only">
            {t('verify.newValue')} — {label}
          </label>
          <input
            id={inputId}
            type="text"
            inputMode="decimal"
            value={decision?.disposition === 'corrected' ? decision.raw : ''}
            placeholder={t('verify.newValuePlaceholder')}
            onChange={(event) => onChange({ disposition: 'corrected', raw: event.target.value })}
            className="num h-8 w-40 rounded-sm border border-border-strong bg-white px-2 text-[13px] text-ink placeholder:text-muted-2"
          />

          {decision?.disposition === 'unreadable' ? (
            <span className="text-[12px] text-muted">{t('verify.unreadableHint')}</span>
          ) : null}
          {decision ? (
            <button
              type="button"
              onClick={() => onChange(undefined)}
              className="text-[12px] text-muted underline underline-offset-2"
            >
              {t('common.cancel')}
            </button>
          ) : null}
        </div>
      )}
    </div>
  )
}
